const UTM_STORAGE_KEY = 'lopeglad_utm_params';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

/**
 * Read UTM params from a query string / URLSearchParams / search string.
 */
export function parseUtmParams(searchOrParams) {
  const params =
    searchOrParams instanceof URLSearchParams
      ? searchOrParams
      : new URLSearchParams(
          typeof searchOrParams === 'string'
            ? searchOrParams.replace(/^\?/, '')
            : ''
        );

  const utm = {};
  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value && String(value).trim() !== '') {
      utm[key] = String(value).trim();
    }
  });
  return utm;
}

export function hasUtmParams(utm) {
  return Boolean(utm && UTM_KEYS.some((key) => utm[key]));
}

/**
 * Persist UTMs (session + local) so they survive navigation to /create-account.
 * Only overwrites when new UTMs are present in the URL.
 */
export function captureUtmFromLocation(search) {
  const fromUrl = parseUtmParams(search);
  if (!hasUtmParams(fromUrl)) {
    return getStoredUtmParams();
  }

  try {
    const payload = JSON.stringify(fromUrl);
    sessionStorage.setItem(UTM_STORAGE_KEY, payload);
    localStorage.setItem(UTM_STORAGE_KEY, payload);
  } catch (_) {
    // ignore storage errors
  }
  return fromUrl;
}

export function getStoredUtmParams() {
  try {
    const raw =
      sessionStorage.getItem(UTM_STORAGE_KEY) ||
      localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const utm = {};
    UTM_KEYS.forEach((key) => {
      if (parsed[key] && String(parsed[key]).trim() !== '') {
        utm[key] = String(parsed[key]).trim();
      }
    });
    return utm;
  } catch (_) {
    return {};
  }
}

/**
 * Append stored/current UTMs to a path or URL (keeps existing query params).
 */
export function appendUtmToUrl(url, utm = getStoredUtmParams()) {
  if (!url || !hasUtmParams(utm)) return url;

  try {
    const isAbsolute = /^https?:\/\//i.test(url) || url.startsWith('lopeglad://');
    const base = isAbsolute ? undefined : window.location.origin;
    const parsed = new URL(url, base);

    UTM_KEYS.forEach((key) => {
      if (utm[key] && !parsed.searchParams.get(key)) {
        parsed.searchParams.set(key, utm[key]);
      }
    });

    if (url.startsWith('lopeglad://')) {
      return parsed.toString();
    }
    if (!isAbsolute) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch (_) {
    return url;
  }
}

/**
 * Play Store install referrer string (for &referrer=...).
 */
export function buildPlayStoreReferrer(utm = getStoredUtmParams()) {
  if (!hasUtmParams(utm)) return null;
  const parts = UTM_KEYS.filter((key) => utm[key]).map(
    (key) => `${encodeURIComponent(key)}=${encodeURIComponent(utm[key])}`
  );
  return parts.join('&');
}

export function appendReferrerToPlayStoreUrl(playStoreUrl, utm = getStoredUtmParams()) {
  const referrer = buildPlayStoreReferrer(utm);
  if (!referrer || !playStoreUrl) return playStoreUrl;
  try {
    const parsed = new URL(playStoreUrl);
    if (!parsed.searchParams.get('referrer')) {
      parsed.searchParams.set('referrer', referrer);
    }
    return parsed.toString();
  } catch (_) {
    return playStoreUrl;
  }
}

export { UTM_KEYS, UTM_STORAGE_KEY };
